import {observer} from 'mobx-react';
import React, {useContext, useEffect, useRef} from 'react';

import {AsyncPriceCourseCardContext} from '@udemy/browse-course';
import {Tracker} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {ExperimentImpressionEvent, useFeatures} from '@udemy/react-experimentation-provider';
import {useStores} from '@udemy/store-provider';
import {
    useActiveSubscriptions,
    ReactSubscriptionsContext,
    SubscriptionPlanContext,
    SubscriptionPlanProductType,
    ConsumerSubscriptionPlan,
    ConsumerSubscriptionGwGStandalonePlan,
    ConsumerSubscriptionGwGPersonalPlan,
} from '@udemy/subscription-graphql';
import {useUDData} from '@udemy/ud-data';

import {
    DELAY_COURSE_CARD_QUICK_VIEW_BOX,
    HIDE_WISHLIST_BUTTON_ON_COURSE_CARD_QUICK_VIEW_BOX,
    PERSONAL_PLAN_COURSE_ENROLLMENT,
    VALUE_PLANSTARTER_PLAN,
    MARKETPLACE_SUBSCRIPTION_OFFER,
    UPDATE_PERSONAL_PLAN_TOPIC_PAGE,
    CERTIFICATION_BUNDLE_ON_TOPIC_CLP,
    CERTIFICATION_BUNDLE_EXPANSION,
    GENERIC_BUNDLES_ON_TOPICS,
    COURSE_GUIDANCE,
    QUICK_VIEW_BOX_FEATURE,
    TOPIC_CAT_SUBCAT_MWEB_CARD_STANDARDIZATION,
    TOPIC_SUBSCRIPTION_UPSELL,
    TOPIC_VOUCHER_MODULE_ABOVE_BUNDLE,
    FBT_FORCE_GO_TO_CART,
    TOPIC_SPECIAL_BUNDLE_PRICING,
    SHOW_CERTIFICATION_VOUCHER_BUNDLE_UNIT,
    SHOW_CERTIFICATION_TOPIC_PAGE,
    USE_GRAPHQL_RECOMMENDATIONS,
    REMOVE_GWG_ENTRY_POINT_TOPIC_PAGE,
} from 'src/lib/experimentation-constants';
import {
    getTopicCatSubcatMwebCardStandardizationConfiguration,
    isMwebStandardizedMobileCourseCardsEnabled,
} from 'src/lib/experimentation-helper';
import {RootStore} from 'src/stores/root-store';

import {CertificationTopic} from './certification-topic.react-component';
import {TopicPageLoader} from './components/topic-page-loader.react-component';
import {TOPIC_PRESET_VALUES, GWG_TOPIC_IDS, CERTIFICATION_TOPIC_IDS} from './constants';
import {FreeTopic} from './free-topic.react-component';
import {Topic as TopicWithRecommendations} from './topic-recommendations.react-component';
import {Topic, TopicPageExperimentVariants, TopicProps} from './topic.react-component';

export type TopicWrapperProps = Omit<TopicProps, 'udData'>;

export const TopicPageExperimentationFeatures = [
    UPDATE_PERSONAL_PLAN_TOPIC_PAGE,
    MARKETPLACE_SUBSCRIPTION_OFFER,
    DELAY_COURSE_CARD_QUICK_VIEW_BOX,
    HIDE_WISHLIST_BUTTON_ON_COURSE_CARD_QUICK_VIEW_BOX,
    PERSONAL_PLAN_COURSE_ENROLLMENT,
    VALUE_PLANSTARTER_PLAN,
    CERTIFICATION_BUNDLE_ON_TOPIC_CLP,
    CERTIFICATION_BUNDLE_EXPANSION,
    GENERIC_BUNDLES_ON_TOPICS,
    COURSE_GUIDANCE,
    QUICK_VIEW_BOX_FEATURE,
    COURSE_GUIDANCE,
    TOPIC_CAT_SUBCAT_MWEB_CARD_STANDARDIZATION,
    TOPIC_SUBSCRIPTION_UPSELL,
    TOPIC_VOUCHER_MODULE_ABOVE_BUNDLE,
    FBT_FORCE_GO_TO_CART,
    TOPIC_SPECIAL_BUNDLE_PRICING,
    SHOW_CERTIFICATION_VOUCHER_BUNDLE_UNIT,
    SHOW_CERTIFICATION_TOPIC_PAGE,
    USE_GRAPHQL_RECOMMENDATIONS,
    REMOVE_GWG_ENTRY_POINT_TOPIC_PAGE,
];

export const TopicWrapper: React.FC<TopicWrapperProps> = observer((props: TopicWrapperProps) => {
    const mobileMaxMedia = useMatchMedia('mobile-max');
    const {Config, request} = useUDData();
    // Match useMatchMedia + request hint (see useDiscoveryLayoutIsMobile): initial client render is null for
    // matchMedia, and without request.isMobile we would omit product-card context while discovery still applies
    // stacked mobile props — cards look condensed/broken.
    const effectiveMobileMax = mobileMaxMedia != null ? mobileMaxMedia : Boolean(request?.isMobile);

    const subscriptionPlans = useContext(ReactSubscriptionsContext);
    const consumerSubscriptionPlan = subscriptionPlans[
        SubscriptionPlanProductType.Consumersubscription
    ] as SubscriptionPlanContext<ConsumerSubscriptionPlan>;
    const activeSubscriptionPlan = useActiveSubscriptions()[0];

    const gwgSubscriptionPlan = subscriptionPlans[
        SubscriptionPlanProductType.GrowWithGoogle
    ] as SubscriptionPlanContext<ConsumerSubscriptionGwGStandalonePlan>;
    const gwgPlusPPSubscriptionPlan = subscriptionPlans[
        SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle
    ] as SubscriptionPlanContext<ConsumerSubscriptionGwGPersonalPlan>;
    const gwgPlans = [gwgSubscriptionPlan, gwgPlusPPSubscriptionPlan];

    const {isLoading, featureVariantAssignments: features} = useFeatures(
        TopicPageExperimentationFeatures,
    );

    // Consider gwg 'available' if any GWG plan is available for purchase, or the user is subscribed to any GWG plan.
    const isGwgAvailable = gwgPlans.some(
        (plan) => !!(plan && !plan.isLoading && ('id' in plan || plan.isSubscriberToSpecificPlan)),
    );
    const showGwgTopicPageEntryPoint =
        features.get(REMOVE_GWG_ENTRY_POINT_TOPIC_PAGE)?.configuration
            ?.showGwgTopicPageEntryPoint ?? true;
    const showGwgCarousel =
        isGwgAvailable &&
        GWG_TOPIC_IDS.includes(props.topic.id) &&
        !Config.brand.has_organization &&
        showGwgTopicPageEntryPoint;

    const topicSubscriptionUpsellFeature = features.get(TOPIC_SUBSCRIPTION_UPSELL);

    const topicCatSubcatMwebCardStandardizationConfig =
        getTopicCatSubcatMwebCardStandardizationConfiguration(features);
    // TODO: GR-56 Remove after topic_cat_subcat_mweb_card_standardization concludes
    const showTopicCatSubcatMwebCardStandardization = isMwebStandardizedMobileCourseCardsEnabled(
        topicCatSubcatMwebCardStandardizationConfig,
    );
    const threeCardVerticalLayoutExperimentIsEnabled = showTopicCatSubcatMwebCardStandardization;
    const useVerticalRecoCourseCardStyle =
        !!effectiveMobileMax && threeCardVerticalLayoutExperimentIsEnabled;

    const experimentProps: TopicPageExperimentVariants = {
        isExperimentConfigsLoading: isLoading,
        showUpdatedPPTopicPage:
            features.get(UPDATE_PERSONAL_PLAN_TOPIC_PAGE)?.configuration?.showUpdatedPPTopicPage ===
            'on'
                ? true
                : false,
        showPersonalPlanBadge:
            features.get(MARKETPLACE_SUBSCRIPTION_OFFER)?.configuration?.show_personal_plan_badge ??
            false,
        // Experiment is archived. We will be removing this soon.
        showCodingExercisesBadge: false,
        showCourseGuidance:
            features.get(COURSE_GUIDANCE)?.configuration?.show_course_guidance_feature ?? false,
        fullQuickViewBoxFeature:
            features.get(QUICK_VIEW_BOX_FEATURE)?.configuration?.feature ?? false,
        topicCatSubcatMwebCardStandardizationFeature: features.get(
            TOPIC_CAT_SUBCAT_MWEB_CARD_STANDARDIZATION,
        ),
        isTopicVoucherModuleAboveBundle: features.get(TOPIC_VOUCHER_MODULE_ABOVE_BUNDLE),
        showTopicSpecialBundlePricing: features.get(TOPIC_SPECIAL_BUNDLE_PRICING),
        certificationVoucherBundleUnitFeature: features.get(SHOW_CERTIFICATION_VOUCHER_BUNDLE_UNIT),
        useVerticalRecoCourseCardStyle,
        threeCardVerticalLayoutExperimentIsEnabled,
        topicSubscriptionUpsellFeature,
    };

    const [rootStore] = useStores([RootStore]);

    const isConsumerSubsSubscriber =
        (consumerSubscriptionPlan &&
            !consumerSubscriptionPlan.isLoading &&
            consumerSubscriptionPlan?.isSubscriber) ??
        false;

    // TODO: CVR-1878 - Remove when recommendation units migration is complete
    const isRecommendationsEnabled =
        features.get(USE_GRAPHQL_RECOMMENDATIONS)?.configuration
            ?.is_graphql_recommendations_enabled ?? false;

    const topicProps = {
        ...props,
        ...experimentProps,
        presetValue: props.presetValue || TOPIC_PRESET_VALUES.ALL,
        relatedPages: props.relatedPages ?? [],
        isConsumerSubsSubscriber,
        isSubsLoading: consumerSubscriptionPlan?.isLoading,
        enableAssessmentsInPersonalPlan: rootStore.headerData.user?.enableAssessmentsInPersonalPlan,
        enableLabsInPersonalPlan: rootStore.headerData.user?.enableLabsInPersonalPlan,
        certification: props.certification,
        certificationBundles: props.certificationBundles,
        showGwgCarousel: showGwgCarousel,
        activeSubscriptionPlan: activeSubscriptionPlan,
        gwgSubscriptionPlan: gwgSubscriptionPlan,
        gwgPlusPPSubscriptionPlan: gwgPlusPPSubscriptionPlan,
    };

    const isCertExperimentTopic = CERTIFICATION_TOPIC_IDS.includes(props.topic.id);
    const certificationTopicPageFeature = features.get(SHOW_CERTIFICATION_TOPIC_PAGE);
    const showCertificationTopicPage =
        certificationTopicPageFeature?.configuration?.showCertificationTopicPage ?? false;

    const hasFiredCertTopicPageImpression = useRef(false);
    useEffect(() => {
        if (
            hasFiredCertTopicPageImpression.current ||
            isLoading ||
            !isCertExperimentTopic ||
            !props.certification ||
            !props.certDescription ||
            !certificationTopicPageFeature
        ) {
            return;
        }
        Tracker.publishEvent(
            new ExperimentImpressionEvent(
                certificationTopicPageFeature.experimentIds,
                certificationTopicPageFeature.isLocalDefault,
            ),
        );
        hasFiredCertTopicPageImpression.current = true;
    }, [
        isLoading,
        isCertExperimentTopic,
        certificationTopicPageFeature,
        props.certification,
        props.certDescription,
    ]);

    if (isCertExperimentTopic) {
        if (isLoading) {
            return <TopicPageLoader />;
        }

        if (showCertificationTopicPage && props.certification && props.certDescription) {
            return <CertificationTopic {...topicProps} />;
        }
    }

    return (
        <>
            {props.presetValue === 'free' ? (
                <FreeTopic {...topicProps} />
            ) : (
                <AsyncPriceCourseCardContext.Provider
                    value={
                        useVerticalRecoCourseCardStyle
                            ? {
                                  useCourseProductCard: true,
                                  hideCourseDescription: true,
                              }
                            : {}
                    }
                >
                    {isRecommendationsEnabled ? (
                        <TopicWithRecommendations {...topicProps} />
                    ) : (
                        <Topic {...topicProps} />
                    )}
                </AsyncPriceCourseCardContext.Provider>
            )}
        </>
    );
});
